import { isPointerDndBackend } from "@ms/uno-utilities/lib/local/DndUtilities";
import * as React from "react";
import { DndContext, useDragLayer } from "react-dnd";
import { Preview } from "react-dnd-multi-backend/dist/esm/components/Preview";
import { taskCardContentWidth } from "../../controls/presentational/legacyPlanner/boardView/BoardViewCommon.styles";
import { TaskBoardCard } from "../../controls/presentational/legacyPlanner/boardView/boardCard/TaskBoardCard";
const isLegacyTaskBoardCardItem = (item)=>typeof item === "object" && item != null && "cardProps" in item;
export class DnDPreviewUtilities {
}
/**
     * Generate a preview for drag and drop when a direct touch/pointer backend is used (see
     * usePointerBackendInDnd). Renders the dragged card using the exact props it was mounted with
     * (captured at drag start on the item), so the preview stays visually faithful to the real card.
     * @param props ItemType defined in useDrag("itemType", source, collect)..., item passed to
     * beginDrag(), and the positioning style react-dnd computes for the drag layer container.
     */ DnDPreviewUtilities.generatePreview = (props)=>{
    const { itemType, item, style } = props;
    switch(itemType){
        case "card":
            {
                if (!isLegacyTaskBoardCardItem(item)) {
                    return /*#__PURE__*/ React.createElement(React.Fragment, null);
                }
                const cardItem = item;
                // TaskBoardCard's root is `width: 100%`, which collapses to a degenerate (near-zero)
                // box when the card isn't inside a column providing that width context - as is the
                // case here, floating in a position: fixed preview layer. Pin an explicit width
                // (matching the board's card content width) so the preview actually renders visibly.
                return /*#__PURE__*/ React.createElement("div", {
                    className: "task-board-card-preview",
                    style: {
                        ...style,
                        width: taskCardContentWidth
                    }
                }, /*#__PURE__*/ React.createElement(TaskBoardCard, {
                    ...cardItem.cardProps,
                    isDragPreview: true
                }));
            }
        default:
            // Unexpected/unsupported drag source for this preview (e.g. column or member card
            // drags, which aren't part of this preview yet). Render nothing rather than throwing.
            return /*#__PURE__*/ React.createElement(React.Fragment, null);
    }
};
/**
 * Backend-agnostic drag preview built on react-dnd's useDragLayer. react-dnd-multi-backend's
 * <Preview> only renders inside a MultiBackend (it relies on the MultiBackend preview portal), so it
 * draws nothing under a direct Touch/pointer backend, which is used when the usePointerBackendInDnd
 * setting is on. This renders the same DnDPreviewUtilities.generatePreview output for that case,
 * positioned under the pointer.
 */ const TouchDragPreview = ()=>{
    const previewRef = React.useRef(null);
    const { isDragging, itemType, item, clientOffset, initialClientOffset, initialSourceClientOffset, monitor } = useDragLayer((dragLayerMonitor)=>({
            isDragging: dragLayerMonitor.isDragging(),
            itemType: dragLayerMonitor.getItemType(),
            item: dragLayerMonitor.getItem(),
            clientOffset: dragLayerMonitor.getClientOffset(),
            initialClientOffset: dragLayerMonitor.getInitialClientOffset(),
            initialSourceClientOffset: dragLayerMonitor.getInitialSourceClientOffset(),
            monitor: dragLayerMonitor
        }));
    if (!isDragging || clientOffset == null) {
        return null;
    }
    // Position the preview under the pointer, preserving the grab offset within the dragged element.
    const parentOffset = initialClientOffset != null && initialSourceClientOffset != null ? {
        x: initialClientOffset.x - initialSourceClientOffset.x,
        y: initialClientOffset.y - initialSourceClientOffset.y
    } : {
        x: 0,
        y: 0
    };
    const transform = `translate(${(clientOffset.x - parentOffset.x).toFixed(1)}px, ${(clientOffset.y - parentOffset.y).toFixed(1)}px)`;
    const style = {
        pointerEvents: "none",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 1000001,
        transform,
        WebkitTransform: transform
    };
    return DnDPreviewUtilities.generatePreview({
        ref: previewRef,
        itemType,
        item,
        style,
        monitor
    });
};
const isLikelyTouchOrPointerBackend = (backend)=>{
    // Prefer the explicit marker set by withDndContext's pointer backend factory: it survives
    // production minification, unlike the constructor-name fallback below. `backend.constructor.name`
    // is unreliable in production because terser mangles class names there (see webpack.main.config.js
    // terser options), which previously caused SafeDnDPreview to silently return null under
    // usePointerBackendInDnd in production builds even though it worked in local/dev builds.
    if (isPointerDndBackend(backend)) {
        return true;
    }
    const backendName = backend?.constructor?.name;
    if (!backendName || backendName === "Object") {
        return false;
    }
    return /touch|pointer/i.test(backendName);
};
/**
 * Renders a drag preview for the legacy planner task board (Basic Plan, My Day, My Tasks), covering
 * both the MultiBackend (HTML5<->Touch autoswitching) case and the direct Touch/pointer backend case
 * used when usePointerBackendInDnd is enabled. Mount once near the root of the tree that's wrapped
 * with the DnD context provider.
 */ export const SafeDnDPreview = ()=>{
    const { dragDropManager } = React.useContext(DndContext);
    if (dragDropManager == null) {
        return null;
    }
    const backend = dragDropManager.getBackend();
    if (!backend) {
        return null;
    }
    // MultiBackend exposes previewEnabled() and manages its own preview portal, so use its <Preview>.
    if (typeof backend.previewEnabled === "function") {
        return /*#__PURE__*/ React.createElement(Preview, null, DnDPreviewUtilities.generatePreview);
    }
    // A direct Touch/pointer backend has no MultiBackend preview portal, so react-dnd-multi-backend's
    // <Preview> renders nothing. Fall back to a useDragLayer-based preview instead.
    if (isLikelyTouchOrPointerBackend(backend)) {
        return /*#__PURE__*/ React.createElement(TouchDragPreview, null);
    }
    return null;
};
